import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Eye, EyeOff, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, password }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setDone(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F7F0] p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#556B2F]" style={{ fontFamily: 'Outfit' }}>Apptio</h1>
        </div>
        {done ? (
          <Card className="border-[#E5E7E1]">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-14 h-14 rounded-full bg-[#3E6641] flex items-center justify-center mx-auto"><Check size={24} className="text-white" /></div>
              <h2 className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Password reset!</h2>
              <p className="text-sm text-[#5C6656]">Your password has been changed successfully.</p>
              <Button onClick={() => navigate('/login')} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white" data-testid="go-login">Go to Login</Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-[#E5E7E1]">
            <CardHeader><CardTitle className="text-xl text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Set new password</CardTitle><CardDescription className="text-[#5C6656]">Enter your new password below.</CardDescription></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>New password</Label>
                  <div className="relative">
                    <Input data-testid="new-password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required className="border-[#E5E7E1] pr-10" placeholder="Min 6 characters" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6656]">{showPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Confirm password</Label>
                  <Input data-testid="confirm-password" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required className="border-[#E5E7E1]" />
                </div>
                {error && <p className="text-sm text-[#E2725B]">{error}</p>}
                <Button type="submit" disabled={loading} className="w-full bg-[#556B2F] hover:bg-[#4A5D23] text-white" data-testid="reset-submit">{loading ? 'Resetting...' : 'Reset Password'}</Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
